import { useId } from 'react'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { CobaltProviderRow } from './CobaltProviderRow'
import type { CobaltProvider, CobaltProviderDay } from './types'

type Changes = Record<string, CobaltProvider['status']>
interface Props {
  providers: CobaltProvider[]
  defaultHours: CobaltProviderDay[]
  search: string
  changes: Changes
  locked: boolean
  discovering: boolean
  onChange: (changes: Changes) => void
}

export function CobaltProviderTable({
  providers,
  defaultHours,
  search,
  changes,
  locked,
  discovering,
  onChange,
}: Props) {
  const selectAllId = useId()
  const names = new Map<string, number>()
  for (const provider of providers) {
    const name = provider.name.trim().toLowerCase()
    names.set(name, (names.get(name) ?? 0) + 1)
  }
  const query = search.trim().toLowerCase()
  const selectedCount = providers.filter(
    (provider) => (changes[provider.id] ?? provider.status) === 'active'
  ).length
  const visible = providers.filter((provider) =>
    `${provider.name} ${provider.npi ?? ''} ${provider.ehr_id}`
      .toLowerCase()
      .includes(search.toLowerCase())
  )
  const selectAll = (selected: boolean) => {
    const status = selected ? 'active' : 'inactive'
    onChange(
      Object.fromEntries(
        providers
          .filter((provider) => status !== provider.status)
          .map((provider) => [provider.id, status])
      )
    )
  }
  return (
    <div>
      <Table
        className="table-fixed"
        containerClassName="max-h-80 rounded-lg border border-border">
        <TableHeader className="sticky top-0 z-10 bg-background">
          <TableRow>
            <TableHead className="w-[40%]">
              <Label
                htmlFor={selectAllId}
                className="flex items-center gap-2 font-medium text-foreground">
                <Checkbox
                  id={selectAllId}
                  aria-label="Select all providers"
                  checked={
                    selectedCount === providers.length && providers.length > 0
                      ? true
                      : selectedCount > 0
                        ? 'indeterminate'
                        : false
                  }
                  disabled={locked || !providers.length}
                  onCheckedChange={(checked) => selectAll(checked === true)}
                />
                Provider
              </Label>
            </TableHead>
            <TableHead>Schedule</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {visible.map((provider) => (
            <CobaltProviderRow
              key={provider.id}
              provider={provider}
              defaultHours={defaultHours}
              pendingStatus={changes[provider.id]}
              locked={locked}
              showIdentifiers={
                (names.get(provider.name.trim().toLowerCase()) ?? 0) > 1 ||
                (!!query && !provider.name.toLowerCase().includes(query))
              }
              onChange={(status) => {
                const next = { ...changes }
                if (status === provider.status) delete next[provider.id]
                else next[provider.id] = status
                onChange(next)
              }}
            />
          ))}
          {!visible.length && (
            <TableRow>
              <TableCell
                colSpan={2}
                className="whitespace-normal text-muted-foreground">
                {providers.length
                  ? 'No providers match your search.'
                  : discovering
                    ? 'Discovering providers…'
                    : 'No providers found. Refresh the directory to discover providers.'}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <p className="mt-2 text-xs text-muted-foreground">
        {selectedCount} of {providers.length} selected
      </p>
    </div>
  )
}
